import React, { useContext, useEffect, useRef, useState, useCallback, memo } from "react";
import { IconButton, InputAdornment, TextField, CircularProgress } from "@mui/material";
import { Search, SearchOff } from "@mui/icons-material";
import Context from "../../contexts/Context";

const SearchInput = ({ label, styles, delay = 750 }) => {
    const { filters, setFilters } = useContext(Context);

    const [localValue, setLocalValue] = useState(filters?.search ?? "");
    const [isLoading, setIsLoading] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        if ((filters?.search ?? "") !== localValue) {
            setLocalValue(filters?.search ?? "");
        }

    }, [filters?.search]);

    const flush = useCallback(
        (nextValue) => {
            setFilters((prev) => ({ ...prev, search: nextValue }));
        },
        [setFilters]
    );

    useEffect(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setIsLoading(true);
        timerRef.current = setTimeout(() => {
            flush(localValue);
            setIsLoading(false);
            timerRef.current = null;
        }, delay);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [localValue, delay, flush]);

    const handleChange = (e) => setLocalValue(e.target.value);

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            if (timerRef.current) clearTimeout(timerRef.current);
            setIsLoading(false);
            flush(localValue);
            timerRef.current = null;
        } else if (e.key === "Escape") {
            handleClear();
        }
    };

    const handleClear = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setLocalValue("");
        setIsLoading(false);
        flush("");
    };

    return (
        <TextField
            fullWidth
            size="small"
            label={label}
            value={localValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            sx={{ maxWidth: "400px", ...styles }}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        {isLoading ? (
                            <CircularProgress size={20} thickness={4} />
                        ) : (
                            <IconButton edge="end" onClick={handleClear}>
                                {localValue ? <SearchOff color="primary" /> : <Search />}
                            </IconButton>
                        )}
                    </InputAdornment>
                ),
            }}
        />
    );
};

export default memo(SearchInput);
